// MediaRecorder helpers for claim-submission voice narration. Plain JS — no
// Vue — so AiInterviewStep.vue owns state/UI and this owns browser-API wiring,
// matching the claimDraft.js / deviceRef.js split in this directory.
//
// Format choice is deliberately left to the browser: motor-assessment-api's
// TranscriptionService.resolveFormat() accepts webm/opus (Chrome, Opera,
// Samsung Internet) and mp4/AAC (Safari) natively by extension or content
// type, so nothing here transcodes.

// 5 minutes; at AUDIO_BITS_PER_SECOND (32kbps mono) a full recording is
// ~1.2MB, well under the backend's 20MB upload cap — the ceiling is a UX
// choice, not a file-size constraint.
export const MAX_RECORDING_SECONDS = 300
export const AUDIO_BITS_PER_SECOND = 32000

// Preference order for MediaRecorder.isTypeSupported(): Opus-in-WebM first
// (Chrome/Opera/Samsung and Safari 18.4+), then MP4/AAC (older Safari).
const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/mp4;codecs=mp4a.40.2',
]

// Overrides are injectable (rather than read straight off window/navigator
// internally) because jsdom's `isSecureContext` behaves as an unforgeable
// browser global in some versions — writes to it are silently ignored
// instead of throwing, which makes the negative case impossible to exercise
// reliably by mutating the DOM global directly in tests.
export function isRecordingSupported({
  secureContext = window.isSecureContext,
  mediaDevices = navigator.mediaDevices,
  mediaRecorder = window.MediaRecorder,
} = {}) {
  return !!(secureContext && mediaDevices?.getUserMedia && mediaRecorder)
}

export function pickSupportedMimeType() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    // Older Safari never implemented isTypeSupported and only ever recorded
    // MP4/AAC, so that's the safe assumption when the check itself is absent.
    return 'audio/mp4'
  }
  return (
    MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type)) || ''
  )
}

export function extensionForMimeType(mimeType = '') {
  return mimeType.includes('mp4') ? 'm4a' : 'webm'
}

export function requestMicrophoneStream() {
  return navigator.mediaDevices.getUserMedia({
    audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
  })
}

export function buildRecorder(stream) {
  const mimeType = pickSupportedMimeType()
  const options = { audioBitsPerSecond: AUDIO_BITS_PER_SECOND }
  if (mimeType) options.mimeType = mimeType
  return new MediaRecorder(stream, options)
}

export function stopStreamTracks(stream) {
  if (stream) stream.getTracks().forEach((track) => track.stop())
}
